/**
 * Form Designer — FHIR-aware semantic fields.
 *
 * PostgreSQL columns and FHIR paths are different things. A form control may
 * bind to a live catalog column (`patients.mrn`), to a FHIR path
 * (`Patient.name`), or to both when a column is the stored projection of a
 * FHIR element. This module is the semantic field catalog: one abstraction
 * that can describe primitives, composites, repeating elements, references
 * and display-only values without inventing a database column.
 *
 * Patient Name is the first composite: `Patient.name` with given/family/
 * prefix/suffix components. It is not a special case and it is not a
 * `patient_name` column. Other FHIR resources can be registered the same way.
 *
 * Database-column presentation stays in lib/formDesignerFields.js. The two
 * layers coexist: FHIR Fields and Database Columns are two views of what a
 * form can bind to, not two competing designers.
 */

export const SEMANTIC_FIELD_TYPES = Object.freeze({
  PRIMITIVE: "primitive",
  COMPLEX: "complex",
  COMPOSITE: "composite",
  REPEATING: "repeating",
  REFERENCE: "reference",
  DISPLAY: "display",
});

/** How a semantic field should be drawn in the toolbox. */
export const SEMANTIC_CONTROL_LABELS = Object.freeze({
  primitive: "Text",
  complex: "Structured",
  composite: "Composite",
  repeating: "Repeating",
  reference: "Lookup",
  display: "Display",
});

/**
 * One semantic field. Callers should go through `semanticField` so every
 * catalog entry has the same shape.
 */
export function semanticField(spec) {
  const resourceType = String(spec?.resourceType || "").trim();
  const fhirPath = String(spec?.fhirPath || "").trim();
  const id = String(spec?.id || "").trim() || fhirPath;
  const fieldType = SEMANTIC_FIELD_TYPES[String(spec?.fieldType || "").toUpperCase()]
    || spec?.fieldType
    || SEMANTIC_FIELD_TYPES.PRIMITIVE;
  const components = (spec?.components || []).map((item) => semanticField({
    ...item,
    resourceType: item.resourceType || resourceType,
    fieldType: item.fieldType || SEMANTIC_FIELD_TYPES.PRIMITIVE,
    editable: item.editable !== false,
  }));
  return {
    id,
    label: String(spec?.label || "").trim() || id,
    resourceType,
    fhirPath,
    fieldType,
    components,
    editable: spec?.editable !== false,
    displayFormat: String(spec?.displayFormat || "").trim(),
    controlType: spec?.controlType || (fieldType === SEMANTIC_FIELD_TYPES.COMPOSITE ? "text" : ""),
    databaseColumn: String(spec?.databaseColumn || "").trim(),
    required: Boolean(spec?.required),
    description: String(spec?.description || "").trim(),
  };
}

/**
 * The binding a control stores. This is presentation + identity, not a copy
 * of a PostgreSQL column definition. `databaseColumn` is optional and only
 * present when the FHIR element has a live stored projection.
 */
export function semanticBinding(field) {
  if (!field?.fhirPath || !field?.resourceType) return null;
  return {
    id: field.id || field.fhirPath,
    label: field.label || field.id,
    resourceType: field.resourceType,
    fhirPath: field.fhirPath,
    fieldType: field.fieldType || SEMANTIC_FIELD_TYPES.PRIMITIVE,
    components: (field.components || []).map((item) => ({
      id: item.id,
      label: item.label,
      fhirPath: item.fhirPath,
      fieldType: item.fieldType || SEMANTIC_FIELD_TYPES.PRIMITIVE,
    })),
    editable: field.editable !== false,
    displayFormat: field.displayFormat || "",
    databaseColumn: field.databaseColumn || "",
  };
}

/** Repair a stored binding so an older form without one still loads. */
export function normalizeSemanticBinding(raw) {
  if (!raw || typeof raw !== "object") return null;
  const resourceType = String(raw.resourceType || raw.resource_type || "").trim();
  const fhirPath = String(raw.fhirPath || raw.fhir_path || "").trim();
  if (!resourceType || !fhirPath) return null;
  return semanticBinding({
    id: raw.id,
    label: raw.label,
    resourceType,
    fhirPath,
    fieldType: raw.fieldType || raw.field_type,
    components: raw.components,
    editable: raw.editable,
    displayFormat: raw.displayFormat || raw.display_format,
    databaseColumn: raw.databaseColumn || raw.database_column,
  });
}

/**
 * A catalog-shaped object `addFieldControl` already understands, plus the
 * semantic binding. `name` is the optional stored column — empty for a
 * composite such as Patient Name.
 */
export function fieldFromSemantic(field) {
  if (!field?.fhirPath) return null;
  return {
    name: field.databaseColumn || "",
    displayLabel: field.label,
    controlType: field.controlType || "text",
    required: Boolean(field.required),
    primaryKey: false,
    semantic: field,
  };
}

export function findSemanticField(id, catalog = FHIR_RESOURCES) {
  const needle = String(id || "").trim();
  if (!needle) return null;
  for (const resource of catalog) {
    for (const field of resource.fields || []) {
      if (field.id === needle || field.fhirPath === needle) return field;
      const child = (field.components || []).find(
        (item) => item.id === needle || item.fhirPath === needle,
      );
      if (child) return { ...child, resourceType: child.resourceType || resource.resourceType };
    }
  }
  return null;
}

export function fhirResourceForTable(table, catalog = FHIR_RESOURCES) {
  const name = String(table || "").trim().toLowerCase();
  if (!name) return null;
  return catalog.find((resource) => resource.table === name) || null;
}

export function matchesSemanticQuery(field, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    field?.label,
    field?.id,
    field?.fhirPath,
    field?.databaseColumn,
    field?.fieldType,
    field?.resourceType,
    ...(field?.components || []).flatMap((item) => [item.label, item.fhirPath, item.id]),
  ];
  return haystack.some((value) => String(value || "").toLowerCase().includes(needle));
}

export function semanticFieldTooltip(field) {
  if (!field) return "";
  const lines = [
    `FHIR path: ${field.fhirPath}`,
    `Resource: ${field.resourceType}`,
    `Field type: ${field.fieldType}`,
  ];
  if (field.databaseColumn) lines.push(`Database field: ${field.databaseColumn}`);
  else lines.push("Not a database column");
  if (field.displayFormat) lines.push(`Display: ${field.displayFormat}`);
  if (field.components?.length) {
    lines.push(`Components: ${field.components.map((item) => item.label).join(", ")}`);
  }
  if (field.description) lines.push(field.description);
  return lines.join("\n");
}

export function semanticControlLabel(field) {
  if (!field) return "";
  if (field.fieldType === SEMANTIC_FIELD_TYPES.COMPOSITE) return SEMANTIC_CONTROL_LABELS.composite;
  if (field.fieldType === SEMANTIC_FIELD_TYPES.REFERENCE) return SEMANTIC_CONTROL_LABELS.reference;
  if (field.fieldType === SEMANTIC_FIELD_TYPES.REPEATING) return SEMANTIC_CONTROL_LABELS.repeating;
  if (field.fieldType === SEMANTIC_FIELD_TYPES.COMPLEX) return SEMANTIC_CONTROL_LABELS.complex;
  if (field.fieldType === SEMANTIC_FIELD_TYPES.DISPLAY) return SEMANTIC_CONTROL_LABELS.display;
  if (field.controlType === "checkbox") return "Yes / No";
  if (field.controlType === "date") return "Date";
  if (field.controlType === "select") return "Choice";
  return SEMANTIC_CONTROL_LABELS.primitive;
}

/**
 * Patient is the first FHIR resource in the Form Designer catalog.
 *
 * Patient Name maps to Patient.name — a HumanName composite — not to a
 * fabricated patients.patient_name column. Simple demographics that already
 * live on `patients` keep their column name so the existing database-backed
 * binding continues to work.
 */
export const PATIENT_SEMANTIC_FIELDS = Object.freeze([
  semanticField({
    id: "Patient.name",
    label: "Patient Name",
    resourceType: "Patient",
    fhirPath: "Patient.name",
    fieldType: SEMANTIC_FIELD_TYPES.COMPOSITE,
    displayFormat: "HumanName",
    controlType: "text",
    required: true,
    description: "FHIR HumanName. Stored as Patient.name rows, not as a patients column.",
    components: [
      {
        id: "Patient.name.given.first",
        label: "First Name",
        fhirPath: "Patient.name.given",
        fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
        displayFormat: "given[0]",
      },
      {
        id: "Patient.name.given.middle",
        label: "Middle Name",
        fhirPath: "Patient.name.given",
        fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
        displayFormat: "given[1]",
      },
      {
        id: "Patient.name.family",
        label: "Last Name",
        fhirPath: "Patient.name.family",
        fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
      },
      {
        id: "Patient.name.prefix",
        label: "Prefix",
        fhirPath: "Patient.name.prefix",
        fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
      },
      {
        id: "Patient.name.suffix",
        label: "Suffix",
        fhirPath: "Patient.name.suffix",
        fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
      },
    ],
  }),
  semanticField({
    id: "Patient.identifier",
    label: "MRN",
    resourceType: "Patient",
    fhirPath: "Patient.identifier",
    fieldType: SEMANTIC_FIELD_TYPES.REPEATING,
    databaseColumn: "mrn",
    controlType: "text",
    required: true,
    description: "Medical record number. FHIR Patient.identifier; stored as patients.mrn.",
  }),
  semanticField({
    id: "Patient.active",
    label: "Active",
    resourceType: "Patient",
    fhirPath: "Patient.active",
    fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
    databaseColumn: "active",
    controlType: "checkbox",
    required: true,
  }),
  semanticField({
    id: "Patient.gender",
    label: "Gender",
    resourceType: "Patient",
    fhirPath: "Patient.gender",
    fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
    databaseColumn: "gender",
    controlType: "select",
    required: true,
  }),
  semanticField({
    id: "Patient.birthDate",
    label: "Birth Date",
    resourceType: "Patient",
    fhirPath: "Patient.birthDate",
    fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
    databaseColumn: "birth_date",
    controlType: "date",
  }),
  semanticField({
    id: "Patient.maritalStatus",
    label: "Marital Status",
    resourceType: "Patient",
    fhirPath: "Patient.maritalStatus",
    fieldType: SEMANTIC_FIELD_TYPES.COMPLEX,
    databaseColumn: "marital_status",
    controlType: "select",
  }),
  semanticField({
    id: "Patient.deceased",
    label: "Deceased",
    resourceType: "Patient",
    fhirPath: "Patient.deceased",
    fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
    databaseColumn: "deceased",
    controlType: "checkbox",
    required: true,
  }),
  semanticField({
    id: "Patient.deceasedDateTime",
    label: "Deceased Date",
    resourceType: "Patient",
    fhirPath: "Patient.deceasedDateTime",
    fieldType: SEMANTIC_FIELD_TYPES.PRIMITIVE,
    databaseColumn: "deceased_at",
    controlType: "date",
  }),
  semanticField({
    id: "Patient.managingOrganization",
    label: "Managing Organization",
    resourceType: "Patient",
    fhirPath: "Patient.managingOrganization",
    fieldType: SEMANTIC_FIELD_TYPES.REFERENCE,
    databaseColumn: "managing_organization_id",
    controlType: "select",
    required: true,
  }),
]);

/**
 * Registered FHIR resources. Only Patient is populated in this pass; the
 * other entries reserve the catalog shape so Encounter, Appointment,
 * Practitioner and Organization can be filled without a second architecture.
 */
export const FHIR_RESOURCES = Object.freeze([
  Object.freeze({
    resourceType: "Patient",
    table: "patients",
    label: "Patient (FHIR)",
    fields: PATIENT_SEMANTIC_FIELDS,
  }),
  Object.freeze({
    resourceType: "Encounter",
    table: "encounters",
    label: "Encounter (FHIR)",
    fields: Object.freeze([]),
  }),
  Object.freeze({
    resourceType: "Appointment",
    table: "appointments",
    label: "Appointment (FHIR)",
    fields: Object.freeze([]),
  }),
  Object.freeze({
    resourceType: "Practitioner",
    table: "providers",
    label: "Practitioner (FHIR)",
    fields: Object.freeze([]),
  }),
  Object.freeze({
    resourceType: "Organization",
    table: "organizations",
    label: "Organization (FHIR)",
    fields: Object.freeze([]),
  }),
]);

export const PATIENT_NAME_FIELD = PATIENT_SEMANTIC_FIELDS[0];
